/**
 * Custom hook for managing notifications.
 */

import { useState, useEffect, useCallback } from 'react';
import { notificationAPI, Notification, NotificationListResponse } from '../services/notificationAPI';
import { webSocketService } from '../services/websocketService';
import { logError, logWarning } from '../utils/errorLogger';

interface UseNotificationsOptions {
  autoFetch?: boolean;
  limit?: number;
  unreadOnly?: boolean;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  isDropdownOpen: boolean;
  openDropdown: () => void;
  closeDropdown: () => void;
}

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const {
    autoFetch = true,
    limit = 20,
    unreadOnly = false
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const maxRetries = 3;

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    // Prevent infinite retries
    if (retryCount >= maxRetries) {
      logWarning('Notifications: Max retry attempts reached, stopping fetch attempts');
      setError('Failed to fetch notifications after multiple attempts');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response: NotificationListResponse = await notificationAPI.getNotifications({
        limit,
        unread_only: unreadOnly
      });
      
      setNotifications(response.data);
      setUnreadCount(response.unread_count);
      setRetryCount(0); // Reset retry count on success
    } catch (err: any) {
      logError('Error fetching notifications', err);
      
      // Handle specific error cases
      if (err.response?.status === 403) {
        setError('Access denied. Please check your authentication.');
        logWarning('Notifications API: 403 Forbidden - Authentication issue');
        setRetryCount(maxRetries); // Stop retrying on auth errors
      } else if (err.response?.status === 401) {
        setError('Please log in to view notifications.');
        logWarning('Notifications API: 401 Unauthorized - Login required');
        setRetryCount(maxRetries); // Stop retrying on auth errors
      } else {
        setError(err.response?.data?.message || 'Failed to fetch notifications');
        setRetryCount(prev => prev + 1); // Increment retry count for other errors
      }
    } finally {
      setLoading(false);
    }
  }, [limit, unreadOnly, retryCount, maxRetries]);

  // Refresh unread count only
  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.unread_count);
    } catch (err: any) {
      logError('Error fetching unread count', err);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationAPI.markAsRead(id);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      logError('Error marking notification as read', err);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      await notificationAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          is_read: true,
          read_at: new Date().toISOString()
        }))
      );
      
      setUnreadCount(0);
    } catch (err: any) {
      logError('Error marking all notifications as read', err);
      throw err;
    }
  }, []);

  // Archive function removed - no action buttons needed

  // Delete function removed - only archive functionality is available

  // Handle real-time notifications
  useEffect(() => {
    const handleNewNotification = (data: any) => {
      // Add new notification to the list
      const newNotification: Notification = {
        id: data.id || `temp_${Date.now()}`,
        type: data.type,
        title: data.title,
        message: data.message,
        priority: data.priority || 'medium',
        is_read: false,
        is_archived: false,
        metadata: data.metadata || {},
        created_at: data.created_at || new Date().toISOString(),
        event_title: data.event_title,
        guest_name: data.guest_name,
        event_details: data.event_details,
        guest_event_details: data.guest_event_details
      };
      
      setNotifications(prev => {
        return [newNotification, ...prev];
      });
      setUnreadCount(prev => prev + 1);
    };

    const handleNotificationUpdate = (data: any) => {
      if (data.type === 'mark_read') {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === data.notification_id 
              ? { ...notification, is_read: true, read_at: data.read_at }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    };

    // Subscribe to WebSocket events first
    const unsubscribeNotification = webSocketService.on('notification', handleNewNotification);
    const unsubscribeNotificationUpdate = webSocketService.on('notification_update', handleNotificationUpdate);

    // Enable WebSocket connection for real-time notifications
    if (!webSocketService.isNotificationConnected) {
      webSocketService.connectNotifications().catch(error => {
        // Error is already logged in connectNotifications
      });
    }

    return () => {
      unsubscribeNotification();
      unsubscribeNotificationUpdate();
    };
  }, []);

  // Auto-fetch notifications on mount
  useEffect(() => {
    if (autoFetch && retryCount < maxRetries) {
      fetchNotifications();
    }
  }, [autoFetch, retryCount, maxRetries, fetchNotifications]);

  // Fallback: Periodic fetch every 10 seconds in case WebSocket fails
  // Only fetch when dropdown is not open to avoid unnecessary refreshes
  useEffect(() => {
    if (isDropdownOpen) {
      return;
    }
    
    const interval = setInterval(() => {
      fetchNotifications();
    }, 10000); // 10 seconds for more responsive updates

    return () => clearInterval(interval);
  }, [isDropdownOpen, fetchNotifications]);

  // Functions to control dropdown state
  const openDropdown = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    refreshUnreadCount,
    isDropdownOpen,
    openDropdown,
    closeDropdown
  };
};